import { mapKeys, mapValues, set } from 'lodash'
import type { AppSpecData, ViewerClientSpecMap } from '@wix/thunderbolt-symbols'
import type {
	Component,
	FetchFn,
	Logger,
	RefSchemas,
	Structure,
	BeckyTopology,
} from '@wix/thunderbolt-becky-types'
import type { BuilderLegacyComponentsMap } from '@wix/thunderbolt-commons'
import type { RefInfo } from '@wix/document-services-types'
import {
	getTopologyUrl,
	replaceUrlBase,
	VIEWER_APPS_URL_KEY,
	VIEWER_ASSETS_URL_KEY,
	VIEWER_APPS_FALLBACK_URL,
	VIEWER_ASSETS_FALLBACK_URL,
} from '@wix/thunderbolt-commons'

const BUILDER_COMP_TYPE = 'EDITOR_REACT_COMPONENT'

export type BuilderRefInfo = Omit<RefInfo, 'isList' | 'isRefOwner' | 'isRelationalSplit' | 'dontSerialize'> & {
	list: boolean
	refOwner: boolean
}

export type BuilderRefSchemas = {
	[map: string]: {
		[dataType: string]: {
			references: Array<BuilderRefInfo>
		}
	}
}

export type CompSchemaRefResponse = {
	referencesMap: BuilderRefSchemas
}

export interface FetchContext {
	fetchFn: FetchFn
	logger?: Logger
	beckyTopology?: BeckyTopology
}

export const getBuilderComponents = (clientSpecMap: ViewerClientSpecMap): Set<string> => {
	const builderComponents = new Set<string>()
	Object.values(clientSpecMap).forEach((app: AppSpecData) => {
		app.components?.forEach((comp) => {
			if (comp.type === BUILDER_COMP_TYPE) {
				builderComponents.add(comp.data.type)
			}
		})
	})
	return builderComponents
}

export const fixBuilderComponent = (component: Component) => {
	set(component, 'builderType', component.componentType)
	set(component, 'componentType', 'Builder')
}

export const fixBuilderLegacyComponent = (component: Component, refType: string) => {
	set(component, 'componentType', refType)
}

export const fixStructureWithBuilderComponents = (structure: Structure, builderComponents: Set<string>) => {
	for (const compId in structure) {
		const comp = structure[compId]
		const isBuilderComponent = comp && builderComponents.has(comp.componentType)
		if (isBuilderComponent) {
			fixBuilderComponent(comp)
		}
	}
	return structure
}

export const fixStructureWithBuilderLegacyComponents = (
	structure: Structure,
	builderLegacyComponentsMap: BuilderLegacyComponentsMap,
) => {
	for (const compId in structure) {
		const comp = structure[compId]
		const refType = builderLegacyComponentsMap[comp.componentType]
		if (refType !== undefined) {
			fixBuilderLegacyComponent(comp, refType)
		}
	}

	return structure
}

export const fixMapNames = (refSchemas: RefSchemas): RefSchemas => {
	return mapKeys(refSchemas, (_value, key) => {
		switch (key) {
			case 'data':
				return 'document_data'
			case 'style':
				return 'theme_data'
			default:
				return key
		}
	})
}

export const builderRefInfoToRefInfo = (builderRefInfo: BuilderRefInfo): RefInfo => {
	const { list, refOwner, ...rest } = builderRefInfo
	return {
		...rest,
		isList: list,
		isRefOwner: refOwner,
	}
}

export const builderRefSchemasToRefSchemas = (refSchemas: BuilderRefSchemas): RefSchemas => {
	return mapValues(refSchemas, (map) => {
		return mapValues(map, (dataTypeObj) => {
			// DM sets without "references", fetch returns with them
			return (dataTypeObj.references || dataTypeObj).map(builderRefInfoToRefInfo)
		})
	})
}

export const getBuilderComponentsApps = (clientSpecMap: ViewerClientSpecMap): Array<AppSpecData> =>
	Object.values(clientSpecMap)
		.filter((app: AppSpecData) => app.components?.some((comp) => comp.type === BUILDER_COMP_TYPE))

export const getBuilderComponentsAppDefIds = (clientSpecMap: ViewerClientSpecMap): Array<string> =>
	getBuilderComponentsApps(clientSpecMap).map((app) => app.appDefinitionId)

const isValidUrl = (appDefId: string, logger?: Logger, url?: string): boolean => {
	try {
		if (!url) {
			throw new Error('Undefined url')
		}
		new URL(url)
		return true
	} catch (error) {
		logger?.warn(`app ${appDefId} has invalid schemaRefUrl`, { appDefId, url, error: { message: error?.message, stack: error?.stack } })
		return false
	}
}

type GetAppIdToCompIdToUrl = {
	builderComponentsAppDefIds: Array<string>
	builderComponentsOnPage?: Set<string>
	clientSpecMap: ViewerClientSpecMap
	logger?: Logger
}

function getAppIdToCompIdToUrl({
	builderComponentsAppDefIds,
	builderComponentsOnPage,
	clientSpecMap,
	logger,
}: GetAppIdToCompIdToUrl): Record<string, Record<string, string>> {
	const isCompInPage = (compType: string): boolean => {
		// TODO: remove after merging specs.thunderbolt.useDynamicRefShemasInCatharsis
		if (!builderComponentsOnPage) {
			return true
		}

		return builderComponentsOnPage.has(compType)
	}

	return Object.values(clientSpecMap)
		.filter((app: AppSpecData) => builderComponentsAppDefIds.includes(app.appDefinitionId))
		.reduce((acc, app) => {
			const validComponents = app.components
				?.filter((comp) =>
					comp.type === BUILDER_COMP_TYPE &&
					isCompInPage(comp.data.type) &&
					isValidUrl(app.appDefinitionId, logger, comp.data.resources.schemaRefsUrl)) || []

			acc[app.appDefinitionId] = validComponents.reduce((compAcc: Record<string, string>, comp) => {
				compAcc[comp.componentId] = comp.data.resources.schemaRefsUrl!
				return compAcc
			}, {})
			return acc
		}, {} as Record<string, Record<string, string>>)
}

export const mergeComponentRefSchemas = (responses: Array<CompSchemaRefResponse>): BuilderRefSchemas => {
	const result: BuilderRefSchemas = {}
	for (const response of responses) {
		const schema = response.referencesMap
		for (const [mapName, mapContent] of Object.entries(schema)) {
			if (!result[mapName]) {
				result[mapName] = {}
			}
			result[mapName] = { ...result[mapName], ...mapContent }
		}
	}
	return result
}

interface FetchResult {
	success: boolean
	compSchemaRefResponse?: CompSchemaRefResponse
}

const fetchSchemaRefUrl = async (url: string, appDefId: string, context: FetchContext): Promise<FetchResult> => {
	const { fetchFn, logger } = context
	try {
		const response = await fetchFn(url)
		if (response.ok) {
			const compSchemaRefResponse = await response.json()
			return { success: true, compSchemaRefResponse }
		}
		throw new Error(`HTTP error ${response.status} - ${response.statusText}`)
	} catch (error) {
		logger?.warn(`app ${appDefId} has failing schemaRefUrl`, { appDefId, url, error: { message: error?.message, stack: error?.stack } })
		return { success: false }
	}
}

function fnv1a64(str: string): string {
	const FNV_OFFSET_BASIS = 0xcbf29ce484222325n
	const FNV_PRIME = 0x100000001b3n
	let hash = FNV_OFFSET_BASIS
	for (let i = 0; i < str.length; i++) {
		hash ^= BigInt(str.charCodeAt(i))
		hash = (hash * FNV_PRIME) & 0xFFFFFFFFFFFFFFFFn
	}
	return hash.toString(16).padStart(16, '0')
}

const calcHashedSchemaRefsUrlKey = (compIdToUrl: Record<string, string>): string => {
	const entries = Object.entries(compIdToUrl)
	entries.sort(([a], [b]) => a.localeCompare(b))
	const canonicalStr = entries.map(([k, v]) => `${k}=${v}`).join('&')
	return fnv1a64(canonicalStr)
}

const getRefsFromS3 = async (compIdToUrl: Record<string, string>, appDefId: string, context: FetchContext): Promise<BuilderRefSchemas | null> => {
	const { fetchFn, logger, beckyTopology } = context
	const hashedKey = calcHashedSchemaRefsUrlKey(compIdToUrl)
	const baseUrl = getTopologyUrl(beckyTopology, VIEWER_ASSETS_URL_KEY, VIEWER_ASSETS_FALLBACK_URL)
	const url = `${baseUrl}/references-map/${hashedKey}`
	try {
		const response = await fetchFn(url)
		if (response.ok) {
			return await response.json()
		}
		throw new Error(`HTTP error ${response.status} - ${response.statusText}`)
	} catch (error) {
		logger?.warn(`Failed to fetch references_map from S3 for app ${appDefId}`, {
			appDefId,
			url,
			hashedKey,
			compIdToUrl,
			error: { message: error?.message, stack: error?.stack },
		})
	}

	return null
}

const getRefsByBulk = async (compIdToUrl: Record<string, string>, appDefId: string, context: FetchContext): Promise<BuilderRefSchemas | null> => {
	const { fetchFn, logger, beckyTopology } = context
	const baseUrl = getTopologyUrl(beckyTopology, VIEWER_APPS_URL_KEY, VIEWER_APPS_FALLBACK_URL)
	const url = `${baseUrl}/v1/schema-collection-references/bulk`
	try {
		const response = await fetchFn(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				app_def_id: appDefId,
				schema_refs_url_map: compIdToUrl,
			}),
		})
		if (response.ok) {
			const data = await response.json()
			return data.referencesMap
		}
		throw new Error(`HTTP error ${response.status} - ${response.statusText}`)
	} catch (error) {
		logger?.error(`Failed to fetch references_map from bulk endpoint for app ${appDefId}`, {
			appDefId,
			url,
			compIdToUrl,
			error: { message: error?.message, stack: error?.stack },
		})
	}

	return null
}

const getRefsByComps = async (appDefId: string, urls: Array<string>, context: FetchContext): Promise<BuilderRefSchemas> => {
	const { beckyTopology, logger } = context
	const baseUrl = getTopologyUrl(beckyTopology, VIEWER_APPS_URL_KEY, VIEWER_APPS_FALLBACK_URL)
	const viewerAppsUrls = urls.map((url) => replaceUrlBase(url, baseUrl, appDefId, logger))
	const fetchResults = await Promise.all(viewerAppsUrls.map((url) => fetchSchemaRefUrl(url, appDefId, context)))
	const successfulResults = fetchResults.filter((result) => result.success)
	if (successfulResults.length === 0) return {}
	const compSchemaRefResponses = successfulResults.map((res) => res.compSchemaRefResponse!)
	return mergeComponentRefSchemas(compSchemaRefResponses)
}

/**
 * Fetches reference schemas for a given app in the following order:
 * 1. S3: Pre-computed references map fetched by hash
 * 2. BulkGetSchemaCollectionReferences: Fetches given urls and saves to S3
 * 3. Individual component URLs: Fetches each component's schema URL separately - last fallback
 */
export const getRefsForAppDefId = async (appDefId: string, urls: Array<string>, compIdToUrl: Record<string, string>, context: FetchContext): Promise<RefSchemas> => {
	let builderRefSchemas = await getRefsFromS3(compIdToUrl, appDefId, context)
	if (!builderRefSchemas) {
		builderRefSchemas = await getRefsByBulk(compIdToUrl, appDefId, context)
	}
	if (!builderRefSchemas) {
		builderRefSchemas = await getRefsByComps(appDefId, urls, context)
	}
	return fixMapNames(builderRefSchemasToRefSchemas(builderRefSchemas))
}

type GetBuilderComponentRefPromises = {
	builderComponentsAppDefIds: Array<string>
	builderComponentsOnPage?: Set<string>
	clientSpecMap: ViewerClientSpecMap
	fetchFn: FetchFn
	logger?: Logger
	beckyTopology?: BeckyTopology
}

export function getBuilderComponentRefPromises({
	builderComponentsAppDefIds,
	builderComponentsOnPage,
	clientSpecMap,
	fetchFn,
	logger,
	beckyTopology
}: GetBuilderComponentRefPromises): Array<{
		appDefId: string
		refsPromise: Promise<RefSchemas>
	}> {
	const context = { fetchFn, logger, beckyTopology }
	const appIdToCompIdToUrl = getAppIdToCompIdToUrl({
		builderComponentsAppDefIds,
		builderComponentsOnPage,
		clientSpecMap,
		logger,
	})
	return builderComponentsAppDefIds.map((appDefId) => {
		const compIdToUrl = appIdToCompIdToUrl[appDefId] || {}
		const appUrls = Object.values(compIdToUrl)
		const refsPromise = appUrls.length > 0
			? getRefsForAppDefId(appDefId, appUrls, compIdToUrl, context)
			: Promise.resolve({})
		return { appDefId, refsPromise }
	})
}

export const formatToAppDefIdToRefs = (
	results: Array<{ appDefId: string; refs: RefSchemas }>,
): Record<string, RefSchemas> => {
	return results.reduce((acc, { appDefId, refs }) => {
		acc[appDefId] = refs
		return acc
	}, {} as Record<string, RefSchemas>)
}

export const getBuilderComponentRefSchemas = async (
	clientSpecMap: ViewerClientSpecMap,
	builderComponentsOnPage: Set<string>,
	context: FetchContext,
): Promise<Record<string, RefSchemas>> => {
	const { fetchFn, logger, beckyTopology } = context
	const builderComponentsAppDefIds = getBuilderComponentsAppDefIds(clientSpecMap)
	const refPromises = getBuilderComponentRefPromises({
		builderComponentsAppDefIds,
		builderComponentsOnPage,
		clientSpecMap,
		fetchFn,
		logger,
		beckyTopology,
	})

	const results = await Promise.all(
		refPromises.map(async ({ appDefId, refsPromise }) => ({ appDefId, refs: await refsPromise })),
	)

	return formatToAppDefIdToRefs(results)
}

export const formatBuilderRefSchemasRecord = (
	builderRefSchemasByAppDefId: Record<string, BuilderRefSchemas>,
): Record<string, RefSchemas> => {
	return Object.entries(builderRefSchemasByAppDefId).reduce((acc, [appDefId, builderRefSchemas]) => {
		acc[appDefId] = fixMapNames(builderRefSchemasToRefSchemas(builderRefSchemas))
		return acc
	}, {} as Record<string, RefSchemas>)
}
